"use client"
import { createContext, useCallback, useContext, useState } from "react"
import type { ReactNode } from "react"

type PageFeedbackContextType = {
  showFeedback: boolean
  setShowFeedback: (show: boolean) => void
  clearShowFeedback: () => void
  pageHasActionBlock: boolean
  setPageHasActionBlock: (hasActionBlock: boolean) => void
  clearPageHasActionBlock: () => void
}

/**
 * Channel for a page to tell the layout-rendered FeedbackBar whether it opted
 * in via its Contentful seo entry (`showFeedbackComponent`).
 *
 * FeedbackBar is mounted by [lang]/layout.tsx, which has no access to the
 * current page's seo entry, so the flag has to travel upward from the page.
 * This mirrors PromotionBannerContext + PageBannerOverride, which solves the
 * same problem for `hideAllSitewidePromotions`.
 *
 * Defaults to false: pages that never mount PageFeedbackOverride — and seo
 * entries predating migration mxp/28.0.js, which return null for the field —
 * keep the bar hidden.
 */
export const PageFeedbackContext = createContext<PageFeedbackContextType>({
  showFeedback: false,
  setShowFeedback: () => {},
  clearShowFeedback: () => {},
  pageHasActionBlock: false,
  setPageHasActionBlock: () => {},
  clearPageHasActionBlock: () => {},
})

export function PageFeedbackProvider({ children }: { children: ReactNode }) {
  const [showFeedback, setShowFeedbackState] = useState(false)
  const [pageHasActionBlock, setPageHasActionBlockState] = useState(false)

  // Stable identities keep PageFeedbackOverride's effect from re-running on
  // every provider render.
  const setShowFeedback = useCallback((show: boolean) => {
    setShowFeedbackState(show)
  }, [])

  const clearShowFeedback = useCallback(() => {
    setShowFeedbackState(false)
  }, [])

  const setPageHasActionBlock = useCallback((hasActionBlock: boolean) => {
    setPageHasActionBlockState(hasActionBlock)
  }, [])

  const clearPageHasActionBlock = useCallback(() => {
    setPageHasActionBlockState(false)
  }, [])

  return (
    <PageFeedbackContext.Provider
      value={{
        showFeedback,
        setShowFeedback,
        clearShowFeedback,
        pageHasActionBlock,
        setPageHasActionBlock,
        clearPageHasActionBlock,
      }}
    >
      {children}
    </PageFeedbackContext.Provider>
  )
}

export function usePageFeedback() {
  return useContext(PageFeedbackContext)
}
